/**
 * Pure helpers for the admin "Invite user" dialog, kept out of the components so they can be tested
 * without rendering anything.
 */

import type { AdminInviteErrorKind } from '../../../types/adminInvites';

/** Mirrors the server's `invalid_price` range: 100 to 10,000,000 cents. */
export const MIN_PRICE_CENTS = 100;
export const MAX_PRICE_CENTS = 10_000_000;

export type PriceParse = { cents: number | null; error: string | null };

/**
 * Dollars as typed ("2500", "$2,500.50") to cents. Blank is not an error: it means the catalog
 * price, and the field is sent without a value.
 */
export const parseDollarsToCents = (raw: string): PriceParse => {
  const cleaned = raw.trim().replace(/[$,\s]/g, '');
  if (!cleaned) return { cents: null, error: null };

  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return { cents: null, error: 'Enter a price in dollars, like 2500.' };
  }

  const cents = Math.round(Number(cleaned) * 100);
  if (cents < MIN_PRICE_CENTS || cents > MAX_PRICE_CENTS) {
    return { cents: null, error: 'Enter a price between $1 and $100,000.' };
  }

  return { cents, error: null };
};

/**
 * The api client falls back to "API request failed: 409 Conflict" when a body carried no message.
 * That is a developer's string, not something to show an admin.
 */
export const serverMessage = (message: string): string | null =>
  message && !message.startsWith('API request failed') ? message : null;

/** Copy for refusals that are the same whichever route they came from. */
const COMMON: Partial<Record<AdminInviteErrorKind, string>> = {
  throttled: 'An invitation went to that address recently. Try again a little later.',
  no_transport: 'Email is not set up right now, so nothing was sent. Try again later.',
  unsupported: 'Inviting is not available on this server yet.',
};

export const commonMessage = (kind: AdminInviteErrorKind, message: string): string =>
  COMMON[kind] ?? serverMessage(message) ?? 'Something went wrong. Please try again.';
